import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Edit3,
  Filter,
  RefreshCcw,
  Search,
  Send,
  Sparkles,
  Star,
  Trash2,
  Zap,
} from "lucide-react";

import PaginationFooter from "../../components/dashboard/production/PaginationFooter";

const tabs = [
  { key: "queue", label: "Queue" },
  { key: "approved", label: "Approved" },
  { key: "needs_edit", label: "Needs Work" },
  { key: "rejected", label: "Rejected" },
  { key: "posted", label: "Posted" },
];

const demoBusiness = {
  name: "Joe’s Coffee Shop",
};

const demoReviews = [
  {
    id: 1,
    authorName: "Sarah Mitchell",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
    content:
      "Absolutely love this place! The staff was friendly, the service was fast, and everything exceeded my expectations.",
    suggestedResponse:
      "Thank you so much, Sarah! We’re thrilled to hear you had such a great experience with our team. We appreciate your support and hope to see you again soon!",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 2,
    authorName: "Chris Parker",
    rating: 3,
    reviewDate: new Date(Date.now() - 1000 * 60 * 34).toISOString(),
    content:
      "Great experience overall, but communication could have been better. I had to wait longer than expected.",
    suggestedResponse:
      "Thank you for your feedback, Chris. We’re glad you had a good overall experience, and we’re sorry the communication and wait time didn’t meet expectations.",
    responseText: "",
    status: "needs_edit",
  },
  {
    id: 3,
    authorName: "Amanda Lewis",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 72).toISOString(),
    content:
      "Amazing service. Everyone was helpful and the coffee was perfect. This is definitely becoming my new regular spot.",
    suggestedResponse:
      "Thank you so much, Amanda! We’re so happy to hear you enjoyed the service and coffee. We look forward to seeing you again soon!",
    responseText:
      "Thank you so much, Amanda! We’re so happy to hear you enjoyed the service and coffee. We look forward to seeing you again soon!",
    status: "posted",
  },
  {
    id: 4,
    authorName: "Michael Johnson",
    rating: 4,
    reviewDate: new Date(Date.now() - 1000 * 60 * 115).toISOString(),
    content:
      "Really nice atmosphere and great drinks. It was a little crowded, but the team handled it well.",
    suggestedResponse:
      "Thank you, Michael! We’re glad you enjoyed the atmosphere and drinks. We appreciate your patience during a busy time.",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 5,
    authorName: "Nina Patel",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 190).toISOString(),
    content:
      "The team was so welcoming and the latte was excellent. Highly recommend stopping in.",
    suggestedResponse:
      "Thank you, Nina! We’re thrilled you enjoyed your latte and felt welcomed by our team.",
    responseText:
      "Thank you, Nina! We’re thrilled you enjoyed your latte and felt welcomed by our team.",
    status: "posted",
  },
  {
    id: 6,
    authorName: "David Brooks",
    rating: 2,
    reviewDate: new Date(Date.now() - 1000 * 60 * 260).toISOString(),
    content:
      "My order was incorrect and it took a while to fix. The staff was polite, but the experience was frustrating.",
    suggestedResponse:
      "Thank you for letting us know, David. We’re sorry your order was incorrect and that it took longer than expected to make right. We appreciate your patience and will use this feedback to improve.",
    responseText: "",
    status: "needs_approval",
  },
  {
    id: 7,
    authorName: "Emily Carter",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 360).toISOString(),
    content:
      "Great coffee, clean space, and friendly people. Exactly what I was hoping for.",
    suggestedResponse:
      "Thank you, Emily! We’re so glad you enjoyed the coffee, space, and service.",
    responseText:
      "Thank you, Emily! We’re so glad you enjoyed the coffee, space, and service.",
    status: "posted",
  },
  {
    id: 8,
    authorName: "Jordan Lee",
    rating: 4,
    reviewDate: new Date(Date.now() - 1000 * 60 * 440).toISOString(),
    content:
      "Good coffee and nice staff. The mobile order pickup area could be a little clearer.",
    suggestedResponse:
      "Thank you, Jordan! We’re glad you enjoyed the coffee and service. We appreciate the feedback about pickup signage and will look into improving it.",
    responseText: "",
    status: "approved",
  },
  {
    id: 9,
    authorName: "Olivia Martin",
    rating: 1,
    reviewDate: new Date(Date.now() - 1000 * 60 * 520).toISOString(),
    content:
      "I waited over 20 minutes and my drink was cold. Not a great experience today.",
    suggestedResponse:
      "We’re sorry to hear that, Olivia. That’s not the experience we want for our customers. Thank you for bringing this to our attention so we can address it with our team.",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 10,
    authorName: "Ben Thompson",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 610).toISOString(),
    content:
      "Best cold brew in town. Always consistent and the employees are super friendly.",
    suggestedResponse:
      "Thank you, Ben! We’re so glad you enjoy the cold brew and appreciate your kind words about our team.",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 11,
    authorName: "Rachel Adams",
    rating: 4,
    reviewDate: new Date(Date.now() - 1000 * 60 * 720).toISOString(),
    content:
      "Cute spot with great pastries. It gets busy but the quality is worth it.",
    suggestedResponse:
      "Thank you, Rachel! We’re happy you enjoyed the pastries and appreciate your patience during busy times.",
    responseText: "",
    status: "rejected",
  },
];

export default function DemoResponses() {
  const reviews = demoReviews;

  const [activeTab, setActiveTab] = useState("queue");
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState("priority");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const stats = useMemo(() => {
    const awaitingApproval = reviews.filter(isAwaitingApproval).length;
    const needsAttention = reviews.filter((r) => Number(r.rating) <= 3).length;
    const generated = reviews.filter((r) => r.suggestedResponse).length;
    const posted = reviews.filter(
      (r) => r.responseText || r.status === "posted",
    ).length;

    return {
      awaitingApproval,
      needsAttention,
      generated,
      posted,
      timeSaved: Math.round(generated * 4),
    };
  }, [reviews]);

  const filteredResponses = useMemo(() => {
    let items = [...reviews];

    switch (activeTab) {
      case "approved":
        items = items.filter((r) => r.status === "approved");
        break;

      case "needs_edit":
        items = items.filter((r) => r.status === "needs_edit");
        break;

      case "rejected":
        items = items.filter((r) => r.status === "rejected");
        break;

      case "posted":
        items = items.filter((r) => r.responseText || r.status === "posted");
        break;

      case "queue":
      default:
        items = items.filter(isAwaitingApproval);
        break;
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();

      items = items.filter(
        (r) =>
          r.authorName?.toLowerCase().includes(term) ||
          r.content?.toLowerCase().includes(term) ||
          r.suggestedResponse?.toLowerCase().includes(term),
      );
    }

    if (sortMode === "priority") {
      items.sort((a, b) => getPriorityScore(b) - getPriorityScore(a));
    }

    if (sortMode === "newest") {
      items.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate));
    }

    if (sortMode === "lowest_rating") {
      items.sort((a, b) => Number(a.rating || 0) - Number(b.rating || 0));
    }

    return items;
  }, [reviews, activeTab, searchTerm, sortMode]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [activeTab, searchTerm, sortMode]);

  const paginatedResponses = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return filteredResponses.slice(start, end);
  }, [filteredResponses, page]);

  const allVisibleSelected =
    paginatedResponses.length > 0 &&
    paginatedResponses.every((review) => selectedIds.includes(review.id));

  function toggleSelected(id) {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  }

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      setSelectedIds((current) =>
        current.filter((id) => !paginatedResponses.some((r) => r.id === id)),
      );
    } else {
      setSelectedIds((current) => [
        ...new Set([...current, ...paginatedResponses.map((r) => r.id)]),
      ]);
    }
  }

  function clearSelected() {
    setSelectedIds([]);
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 mb-3">
            <Sparkles size={14} />
            Workflow center
          </div>

          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Response Queue
          </h1>

          <p className="text-slate-500 mt-1">
            Review, approve, edit, and post prepared responses for{" "}
            <span className="font-medium text-slate-700">
              {demoBusiness.name}
            </span>
            .
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2">
            <Zap size={16} />
            Automation Settings
          </button>

          <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2">
            <Sparkles size={16} />
            Prepare Pending
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <WorkflowStatCard
          title="Awaiting Approval"
          value={stats.awaitingApproval}
          subtitle="Drafts ready to review"
          icon={<Clock size={22} className="text-orange-600" />}
          iconBg="bg-orange-100"
        />

        <WorkflowStatCard
          title="Needs Attention"
          value={stats.needsAttention}
          subtitle="Low-rating reviews flagged"
          icon={<AlertCircle size={22} className="text-red-600" />}
          iconBg="bg-red-100"
        />

        <WorkflowStatCard
          title="Responses Posted"
          value={stats.posted}
          subtitle="+14 this week"
          icon={<CheckCircle size={22} className="text-green-600" />}
          iconBg="bg-green-100"
        />

        <WorkflowStatCard
          title="Estimated Time Saved"
          value={`${stats.timeSaved}m`}
          subtitle="Based on prepared drafts"
          icon={<Sparkles size={22} className="text-indigo-600" />}
          iconBg="bg-indigo-100"
        />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-4 gap-6 xl:items-start">
        <div className="xl:col-span-3 space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const active = activeTab === tab.key;

                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                      active
                        ? "bg-indigo-600 text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="relative flex-1">
                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search reviewer, review, or keywords..."
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter size={17} className="text-slate-400" />

                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                >
                  <option value="priority">Priority first</option>
                  <option value="newest">Newest first</option>
                  <option value="lowest_rating">Lowest rating first</option>
                </select>
              </div>
            </div>

            {filteredResponses.length > 0 && (
              <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
                <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Select visible responses
                </label>

                <span className="text-sm text-slate-500">
                  {filteredResponses.length} item
                  {filteredResponses.length === 1 ? "" : "s"}
                </span>
              </div>
            )}
          </div>

          {selectedIds.length > 0 && (
            <BulkActionsBar
              selectedCount={selectedIds.length}
              onClear={clearSelected}
            />
          )}

          {filteredResponses.length > 0 ? (
            <>
              <div className="space-y-3">
                {paginatedResponses.map((review) => (
                  <ResponseQueueCard
                    key={review.id}
                    review={review}
                    selected={selectedIds.includes(review.id)}
                    onToggleSelected={() => toggleSelected(review.id)}
                  />
                ))}
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <PaginationFooter
                  page={page}
                  pageSize={pageSize}
                  totalItems={filteredResponses.length}
                  onPageChange={setPage}
                />
              </div>
            </>
          ) : (
            <EmptyState activeTab={activeTab} />
          )}
        </div>

        <aside className="space-y-4 xl:sticky xl:top-24 xl:self-start">
          <AutomationPanel />
          <ActivityPanel />
        </aside>
      </section>
    </div>
  );
}

function WorkflowStatCard({ title, value, subtitle, icon, iconBg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <h3 className="text-3xl font-bold text-slate-900 mt-2">{value}</h3>
          <p className="text-sm text-slate-500 mt-2">{subtitle}</p>
        </div>

        <div
          className={`h-11 w-11 rounded-2xl flex items-center justify-center ${iconBg}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function BulkActionsBar({ selectedCount, onClear }) {
  return (
    <div className="xl:sticky xl:top-24 xl:self-start z-20 bg-slate-900 text-white rounded-2xl shadow-lg px-4 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
      <p className="text-sm font-medium">
        {selectedCount} response{selectedCount === 1 ? "" : "s"} selected
      </p>

      <div className="flex flex-wrap gap-2">
        <button className="bg-white text-slate-900 hover:bg-slate-100 px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <CheckCircle size={15} />
          Approve Selected
        </button>

        <button className="bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <RefreshCcw size={15} />
          Regenerate
        </button>

        <button className="bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <Trash2 size={15} />
          Reject
        </button>

        <button
          onClick={onClear}
          className="text-slate-300 hover:text-white px-3 py-2 rounded-xl text-sm font-medium transition"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

function ResponseQueueCard({ review, selected, onToggleSelected }) {
  const priority = getPriority(review);
  const confidence = getConfidence(review);
  const sentiment = getSentiment(review);

  return (
    <article
      className={`bg-white border rounded-2xl shadow-sm p-4 transition ${
        priority === "High"
          ? "border-red-200"
          : selected
            ? "border-indigo-300 ring-4 ring-indigo-50"
            : "border-slate-200"
      }`}
    >
      <div className="flex gap-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-slate-900">
                  {review.authorName || "Customer"}
                </h3>

                <span className="text-sm text-slate-400">
                  {formatDate(review.reviewDate)}
                </span>

                {priority === "High" && (
                  <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 border border-red-100">
                    High Priority
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1 mt-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={index}
                    size={15}
                    className={
                      index < Number(review.rating || 0)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-slate-300"
                    }
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge label={sentiment} />
              <Badge label={`${confidence} confidence`} />
              <StatusBadge status={review.status} />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Customer Review
              </p>
              <p className="text-sm leading-6 text-slate-700 mt-2">
                {truncate(review.content, 260)}
              </p>
            </div>

            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <div className="flex items-center gap-2 text-indigo-700">
                <Sparkles size={15} />
                <p className="text-xs font-semibold uppercase tracking-wide">
                  Suggested Response
                </p>
              </div>

              <p className="text-sm leading-6 text-indigo-950 mt-2">
                {truncate(
                  review.suggestedResponse || review.responseText,
                  280,
                ) || "No draft available yet."}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <p className="text-xs text-slate-400">
              Suggested tone: Professional · Source: Google Business Profile
            </p>

            <div className="flex flex-wrap gap-2">
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
                <CheckCircle size={15} />
                Approve
              </button>

              <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
                <Edit3 size={15} />
                Edit
              </button>

              <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
                <RefreshCcw size={15} />
                Regenerate
              </button>

              <button className="border border-red-200 bg-white hover:bg-red-50 text-red-700 px-3 py-2 rounded-xl text-sm font-medium transition">
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function AutomationPanel() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
      <div className="flex items-center gap-2">
        <Zap size={18} className="text-indigo-600" />
        <h2 className="font-semibold text-slate-900">Automation Rules</h2>
      </div>

      <div className="mt-4 space-y-3">
        <AutomationToggle
          title="Auto-approve 5-star drafts"
          description="Safe positive reviews can be approved faster."
          enabled
        />

        <AutomationToggle
          title="Flag low ratings"
          description="1–3 star reviews stay manual."
          enabled
        />

        <AutomationToggle
          title="Auto-post approved responses"
          description="Post after approval without extra steps."
          enabled={false}
        />
      </div>
    </div>
  );
}

function AutomationToggle({ title, description, enabled }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-3">
      <div>
        <p className="text-sm font-medium text-slate-900">{title}</p>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>

      <div
        className={`h-6 w-10 rounded-full p-1 shrink-0 ${
          enabled ? "bg-indigo-600" : "bg-slate-200"
        }`}
      >
        <div
          className={`h-4 w-4 rounded-full bg-white transition ${
            enabled ? "translate-x-4" : ""
          }`}
        />
      </div>
    </div>
  );
}

function ActivityPanel() {
  const activity = [
    {
      icon: Sparkles,
      title: "4 drafts prepared",
      description: "Ready for approval",
      time: "Just now",
    },
    {
      icon: AlertCircle,
      title: "Low rating flagged",
      description: "Manual review recommended",
      time: "12 min ago",
    },
    {
      icon: Send,
      title: "Response posted",
      description: "Owner response published",
      time: "1 hr ago",
    },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
      <h2 className="font-semibold text-slate-900">RevUShield Activity</h2>

      <div className="mt-4 space-y-4">
        {activity.map((item, index) => {
          const Icon = item.icon;

          return (
            <div key={`${item.title}-${index}`} className="flex gap-3">
              <div className="h-9 w-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                <Icon size={17} />
              </div>

              <div>
                <p className="text-sm font-medium text-slate-900">
                  {item.title}
                </p>

                <p className="text-sm text-slate-500">{item.description}</p>

                <p className="text-xs text-slate-400 mt-1">{item.time}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyState({ activeTab }) {
  const messages = {
    queue: "No responses are waiting for approval.",
    approved: "No approved responses yet.",
    needs_edit: "No responses currently need edits.",
    rejected: "No rejected responses yet.",
    posted: "No posted responses yet.",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
        <Sparkles size={22} />
      </div>

      <h2 className="text-lg font-semibold text-slate-900 mt-4">
        Queue is clear
      </h2>

      <p className="text-slate-500 mt-2">
        {messages[activeTab] || "Nothing in this category yet."}
      </p>
    </div>
  );
}

function Badge({ label }) {
  return (
    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
      {label}
    </span>
  );
}

function StatusBadge({ status }) {
  const mapped = mapStatus(status);

  const variants = {
    needs_response: "bg-orange-50 text-orange-700 border-orange-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    posted: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    needs_edit: "bg-purple-50 text-purple-700 border-purple-200",
  };

  const labels = {
    needs_response: "Needs Approval",
    approved: "Approved",
    posted: "Posted",
    rejected: "Rejected",
    needs_edit: "Needs Work",
  };

  return (
    <span
      className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
        variants[mapped] || variants.needs_response
      }`}
    >
      {labels[mapped] || "Needs Approval"}
    </span>
  );
}

function isAwaitingApproval(review) {
  return (
    review.suggestedResponse &&
    !review.responseText &&
    review.status !== "posted" &&
    review.status !== "approved" &&
    review.status !== "rejected"
  );
}

function getPriority(review) {
  if (Number(review.rating) <= 3) return "High";
  if (review.status === "needs_edit") return "Medium";
  return "Normal";
}

function getPriorityScore(review) {
  if (Number(review.rating) <= 2) return 4;
  if (Number(review.rating) === 3) return 3;
  if (review.status === "needs_edit") return 2;
  return 1;
}

function getSentiment(review) {
  const rating = Number(review.rating || 0);

  if (rating >= 4) return "Positive";
  if (rating === 3) return "Mixed";
  return "Negative";
}

function getConfidence(review) {
  const rating = Number(review.rating || 0);
  const hasDraft = Boolean(review.suggestedResponse);

  if (!hasDraft) return "Low";
  if (rating >= 4) return "High";
  if (rating === 3) return "Medium";
  return "Manual";
}

function formatDate(date) {
  if (!date) return "Unknown date";

  return new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function truncate(text, maxLength) {
  if (!text) return "";

  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength).trim()}...`;
}

function mapStatus(status) {
  if (!status) return "needs_response";

  if (
    status === "pending_response" ||
    status === "draft_generated" ||
    status === "needs_approval" ||
    status === "new"
  ) {
    return "needs_response";
  }

  return status;
}
